import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Lightbulb, Rocket, Users, Globe, Bot, ShoppingCart, Plane, Star, Briefcase, Heart, GraduationCap, TrendingUp, Mail, MapPin, Phone, Calendar, UserPlus, FolderKanban, Flag, ArrowRight, X, Link2, CheckCircle, AlertCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import '@/lib/i18n'
import Button from '@/components/ui/Button'
import Container from '@/components/ui/Container'
import Section from '@/components/ui/Section'
import Card from '@/components/ui/Card'
import { MorphingText } from '@/components/ui/morphing-text'
import { useTheme } from '@/context/ThemeContext'
import { supabase } from '@/lib/supabase'
import { useSeo } from '@/lib/seo'

export default function HomePage() {
  const { t } = useTranslation(['translation', 'home'])
  const { theme } = useTheme()
  const location = useLocation()

  useSeo({
    title: 'Bassili Group | AI & eCommerce Solutions — BassiliChat AI & BassiliTrade',
    description:
      'Bassilichat Inc. builds AI and eCommerce technology from Montreal, Canada. Compare 7 AI models with BassiliChat AI (free plan, paid from $9/mo) and sell across marketplaces with BassiliTrade.',
    path: '/',
  })
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const jobPostings = [
    {
      id: 'fullstack-001',
      i18nKey: 'home:careers.jobPostings.fullstackDeveloper',
      isOpen: false,
    },
  ]

  const [selectedJob, setSelectedJob] = useState<string | null>(null)
  const [expandedJob, setExpandedJob] = useState<string | null>(null)
  const [appForm, setAppForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    linkedIn: '',
    portfolio: '',
    github: '',
    yearsExperience: '',
    currentRole: '',
    noticePeriod: '',
    workAuthorization: '',
    coverLetter: '',
    salaryExpectation: '',
    heardAboutUs: '',
  })
  const [resumeLink, setResumeLink] = useState('')
  const [coverLetterLink, setCoverLetterLink] = useState('')
  const [appSubmitting, setAppSubmitting] = useState(false)
  const [appStatus, setAppStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleAppChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setAppForm({ ...appForm, [e.target.name]: e.target.value })
  }

  const handleAppSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resumeLink.trim()) return
    setAppSubmitting(true)
    setAppStatus('idle')

    const selectedJobPosting = jobPostings.find(j => j.id === selectedJob)
    const payload = {
      ...appForm,
      jobTitle: selectedJobPosting ? t(`${selectedJobPosting.i18nKey}.title`) : '',
      resumeLink,
      ...(coverLetterLink.trim() ? { coverLetterLink } : {}),
    }

    try {
      const res = await fetch('https://formspree.io/f/mpqbpkbj', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      })
      if (res.ok) {
        setAppStatus('success')
        setAppForm({ fullName: '', email: '', phone: '', linkedIn: '', portfolio: '', github: '', yearsExperience: '', currentRole: '', noticePeriod: '', workAuthorization: '', coverLetter: '', salaryExpectation: '', heardAboutUs: '' })
        setResumeLink('')
        setCoverLetterLink('')
      } else {
        setAppStatus('error')
      }
    } catch {
      setAppStatus('error')
    } finally {
      setAppSubmitting(false)
    }
  }

  useEffect(() => {
    // Initialize i18n
  }, [])

  // Handle hash navigation when coming from other pages or on refresh
  useEffect(() => {
    // Always scroll to top first on mount/refresh
    window.scrollTo(0, 0)
    
    const hash = location.hash || window.location.hash
    if (hash) {
      // Remove the # from the hash
      const sectionId = hash.substring(1)
      // Wait for the page to render, then scroll to the section
      const scrollToHash = () => {
        const element = document.getElementById(sectionId)
        if (element) {
          const offset = 80 // Account for navbar height
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
          return true
        }
        return false
      }

      // Try after a delay to ensure page is rendered
      setTimeout(() => {
        if (!scrollToHash()) {
          setTimeout(() => {
            scrollToHash()
          }, 100)
        }
      }, 100)
    }
  }, [location])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
          },
        ])

      if (error) throw error

      setSubmitStatus('success')
      setFormData({ name: '', email: '', subject: '', message: '' })
      setTimeout(() => setSubmitStatus('idle'), 5000)
    } catch (error) {
      console.error('Error submitting form:', error)
      setSubmitStatus('error')
      setTimeout(() => setSubmitStatus('idle'), 5000)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setNewsletterStatus('idle')

    try {
      const { error } = await supabase
        .from('newsletter_subscriptions')
        .insert([{ email: newsletterEmail }])

      if (error) throw error

      setNewsletterStatus('success')
      setNewsletterEmail('')
      setTimeout(() => setNewsletterStatus('idle'), 5000)
    } catch (error) {
      console.error('Error subscribing to newsletter:', error)
      setNewsletterStatus('error')
      setTimeout(() => setNewsletterStatus('idle'), 5000)
    }
  }

  const values = [
    {
      icon: <Lightbulb className="w-6 h-6" />,
      titleKey: 'home:about.values.innovation.title',
      descriptionKey: 'home:about.values.innovation.description',
    },
    {
      icon: <Users className="w-6 h-6" />,
      titleKey: 'home:about.values.peopleCentered.title',
      descriptionKey: 'home:about.values.peopleCentered.description',
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      titleKey: 'home:about.values.growth.title',
      descriptionKey: 'home:about.values.growth.description',
    },
    {
      icon: <Globe className="w-6 h-6" />,
      titleKey: 'home:about.values.globalImpact.title',
      descriptionKey: 'home:about.values.globalImpact.description',
    },
  ]

  const products = [
    {
      icon: <img src="/logo.svg" alt="Bassilichat AI" className="w-8 h-8" />,
      titleKey: 'home:products.bassiliChat.title',
      taglineKey: 'home:products.bassiliChat.tagline',
      descriptionKey: 'home:products.bassiliChat.description',
      status: 'active',
      gradient: 'from-blue-400 via-blue-500 to-blue-600',
      url: '/bassilichatai',
      isInternal: true,
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      titleKey: 'home:products.bassiliTrade.title',
      taglineKey: 'home:products.bassiliTrade.tagline',
      descriptionKey: 'home:products.bassiliTrade.description',
      status: 'active',
      gradient: 'from-green-400 via-teal-500 to-teal-600',
      url: '/bassilitrade',
      isInternal: true,
    },
    {
      icon: <Plane className="w-8 h-8" />,
      titleKey: 'home:products.bassiliTravel.title',
      taglineKey: 'home:products.bassiliTravel.tagline',
      descriptionKey: 'home:products.bassiliTravel.description',
      status: 'coming-soon',
      gradient: 'from-orange-400 via-orange-500 to-red-500',
      url: '#',
    },
  ]

  const benefits = [
    {
      icon: <Briefcase className="w-8 h-8" />,
      titleKey: 'home:careers.benefits.flexible.title',
      descriptionKey: 'home:careers.benefits.flexible.description',
    },
    {
      icon: <Heart className="w-8 h-8" />,
      titleKey: 'home:careers.benefits.health.title',
      descriptionKey: 'home:careers.benefits.health.description',
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      titleKey: 'home:careers.benefits.learning.title',
      descriptionKey: 'home:careers.benefits.learning.description',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      titleKey: 'home:careers.benefits.potential.title',
      descriptionKey: 'home:careers.benefits.potential.description',
    },
  ]

  return (
    <div className="relative">
      {/* Hero Section - Modern Split Design */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-blue-950 dark:to-indigo-950 pt-20">
        {/* Animated Grid Pattern Background */}
        <div className="absolute inset-0 opacity-20 dark:opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #0072ff 1px, transparent 1px), linear-gradient(to bottom, #0072ff 1px, transparent 1px)`,
              backgroundSize: '4rem 4rem',
            }}
          />
        </div>

        {/* Floating Geometric Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Large Circle */}
          <motion.div
            className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-gradient-to-br from-blue-400/30 to-purple-400/30 dark:from-blue-500/20 dark:to-purple-500/20 blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
              x: [0, 50, 0],
              y: [0, 30, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Medium Square */}
          <motion.div
            className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-gradient-to-br from-cyan-400/30 to-teal-400/30 dark:from-cyan-500/20 dark:to-teal-500/20 blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -180, 0],
              x: [0, -40, 0],
              y: [0, 40, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Small Triangle Effect */}
          <motion.div
            className="absolute top-1/2 left-1/3 w-32 h-32 bg-gradient-to-br from-orange-400/30 to-pink-400/30 dark:from-orange-500/20 dark:to-pink-500/20 blur-3xl"
            animate={{
              scale: [1, 1.4, 1],
              x: [0, 60, 0],
              y: [0, -50, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        <Container className="relative z-10 py-8 md:py-12">
          <div className="grid md:grid-cols-[1.2fr_0.8fr] lg:grid-cols-[1.1fr_0.9fr] gap-6 md:gap-8 lg:gap-16 items-center">
            {/* Left Side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-4 min-w-0"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-block"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20 border border-blue-500/20 dark:border-blue-500/30">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                  </span>
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{t('home:hero.badge')}</span>
                </div>
              </motion.div>

              {/* Main Heading with Morphing Text */}
              <div className="min-w-0">
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-3 leading-tight break-words overflow-wrap-anywhere"
                  style={{ paddingBottom: '0.25em' }}
                >
                  <MorphingText
                    texts={t('home:hero.morphingTexts', { returnObjects: true }) as string[]}
                    duration={5000}
                    className="block bg-clip-text text-transparent break-words"
                    style={{
                      backgroundImage: theme === 'dark' 
                        ? 'linear-gradient(to right, #3498db, #ff6b35)'
                        : 'linear-gradient(to right, #667db6, #0082c8, #0082c8, #667db6)',
                    }}
                  />
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="text-lg sm:text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-3 font-medium leading-relaxed break-words"
                >
                  {t('home:hero.subtitle')}
                </motion.p>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-4 break-words"
                >
                  {t('home:hero.description')}
                </motion.p>
              </div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <button
                  onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
                  className="group relative px-8 py-4 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-600 dark:from-primary-500 dark:via-primary-600 dark:to-accent-500 text-white rounded-xl font-semibold text-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-primary-500/50"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {t('home:hero.cta1')}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-accent-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>

                <button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="group px-8 py-4 bg-white dark:bg-slate-800 text-primary-600 dark:text-primary-400 rounded-xl font-semibold text-lg border-2 border-primary-600 dark:border-primary-400 hover:bg-primary-50 dark:hover:bg-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  <span className="flex items-center justify-center gap-2">
                    {t('home:hero.cta2')}
                    <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </span>
                </button>
              </motion.div>

              {/* Stats Pills */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="flex flex-wrap gap-4 pt-4"
              >
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm shadow-md border border-gray-200 dark:border-slate-700">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('home:hero.stats.years')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm shadow-md border border-gray-200 dark:border-slate-700">
                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('home:hero.stats.team')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm shadow-md border border-gray-200 dark:border-slate-700">
                  <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('home:hero.stats.countries')}</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Side - Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hidden md:flex relative items-center justify-center overflow-visible mt-6 md:mt-0 flex-shrink-0"
            >
              {/* 3D Card Stack Effect */}
              <div className="relative w-full max-w-[200px] sm:max-w-[240px] md:max-w-[280px] lg:max-w-lg flex justify-center md:justify-end" style={{ minHeight: '200px' }}>
                {/* Card 3 - Back */}
                <motion.div
                  animate={{
                    y: [0, -15, 0],
                    rotate: [3, 5, 3],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="absolute top-4 right-1 sm:top-6 sm:right-2 md:top-8 md:right-4 lg:top-12 lg:right-8 w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-80 lg:h-80 rounded-3xl bg-gradient-to-br from-orange-400 to-red-400 dark:from-orange-500 dark:to-red-500 opacity-50 blur-sm shadow-2xl"
                />

                {/* Card 2 - Middle */}
                <motion.div
                  animate={{
                    y: [0, -20, 0],
                    rotate: [-2, 0, -2],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="absolute top-2 right-0.5 sm:top-3 sm:right-1 md:top-4 md:right-2 lg:top-6 lg:right-4 w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-80 lg:h-80 rounded-3xl bg-gradient-to-br from-purple-400 to-pink-400 dark:from-purple-500 dark:to-pink-500 opacity-70 shadow-2xl"
                />

                {/* Card 1 - Front */}
                <motion.div
                  animate={{
                    y: [0, -25, 0],
                    rotate: [0, -3, 0],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-80 lg:h-80 rounded-3xl bg-gradient-to-br from-blue-500 to-cyan-500 dark:from-blue-600 dark:to-cyan-600 shadow-2xl p-2 sm:p-3 md:p-4 lg:p-8 flex flex-col items-center justify-center text-white"
                >
                  {/* Decorative Elements */}
                  <div className="absolute top-2 right-2 sm:top-4 sm:right-4 w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 lg:w-16 lg:h-16 rounded-full bg-white/20 backdrop-blur-sm" />
                  <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-24 lg:h-24 rounded-full bg-white/10 backdrop-blur-sm" />

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                      className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-24 lg:h-24 mx-auto mb-1 sm:mb-2 md:mb-3 lg:mb-6 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-lg flex items-center justify-center"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-12 lg:h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </motion.div>
                    <h3 className="text-xs sm:text-sm md:text-base lg:text-2xl font-bold mb-0.5 sm:mb-1 lg:mb-2">{t('home:hero.card.title')}</h3>
                    <p className="text-white/80 text-[8px] sm:text-[10px] md:text-xs lg:text-base">{t('home:hero.card.subtitle')}</p>
                  </div>
                </motion.div>

                {/* Floating Particles */}
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-3 md:h-3 bg-blue-400/60 rounded-full blur-sm"
                    style={{
                      left: `${20 + Math.random() * 60}%`,
                      top: `${20 + Math.random() * 60}%`,
                    }}
                    animate={{
                      y: [0, -30, 0],
                      opacity: [0.3, 0.8, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3 + i * 0.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: i * 0.3,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </Container>

        {/* Bottom Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-24 fill-current text-white dark:text-slate-900" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
          </svg>
        </div>
      </section>

      {/* About Section - Redesigned with Statistics */}
      <Section id="about" className="bg-gradient-to-br from-gray-50 via-white to-blue-100/50 dark:from-slate-800 dark:via-slate-900 dark:to-blue-950/50 relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 opacity-40 dark:opacity-30">
          <div className="absolute top-10 right-0 w-[400px] h-[400px] bg-gradient-to-br from-primary-300 to-accent-300 dark:from-primary-500/50 dark:to-accent-500/50 rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-purple-300 to-pink-300 dark:from-purple-500/50 dark:to-pink-500/50 rounded-full filter blur-3xl" />
        </div>

        <Container className="relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 dark:text-white leading-tight">
              {t('home:about.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-300">
              {t('home:about.subtitle')}
            </p>
          </motion.div>

          {/* Two-Column Layout: Mission + Stats | Values */}
          <div className="grid lg:grid-cols-2 gap-4 mb-4 items-stretch">
            {/* Left Column: Mission Statement & Statistics */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col h-full"
            >
              <div className="space-y-4 flex-1 flex flex-col">
                {/* Mission Statement Card */}
                <div className="bg-gradient-to-br from-primary-100 to-accent-100 dark:from-slate-900 dark:to-slate-800 rounded-xl p-4 shadow-lg border border-primary-200 dark:border-slate-700 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold mb-2 text-primary-600 dark:text-primary-400">
                    {t('home:about.mission.title')}
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed flex-1">
                    {t('home:about.mission.description')}
                  </p>
                </div>

                {/* Statistics Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Years in Business */}
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-md border border-gray-100 dark:border-slate-800 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-200 to-blue-300 dark:from-blue-500/50 dark:to-blue-400/50 text-blue-600 dark:text-blue-300 mb-2">
                      <Calendar className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                      {t('home:about.stats.years')}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('home:about.stats.yearsLabel')}
                    </div>
                  </div>

                  {/* Team Members */}
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-md border border-gray-100 dark:border-slate-800 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-green-200 to-green-300 dark:from-green-500/50 dark:to-green-400/50 text-green-600 dark:text-green-300 mb-2">
                      <UserPlus className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                      {t('home:about.stats.team')}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('home:about.stats.teamLabel')}
                    </div>
                  </div>

                  {/* Projects Completed */}
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-md border border-gray-100 dark:border-slate-800 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-purple-200 to-purple-300 dark:from-purple-500/50 dark:to-purple-400/50 text-purple-600 dark:text-purple-300 mb-2">
                      <FolderKanban className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                      {t('home:about.stats.projects')}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('home:about.stats.projectsLabel')}
                    </div>
                  </div>

                  {/* Countries Served */}
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-md border border-gray-100 dark:border-slate-800 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-orange-200 to-orange-300 dark:from-orange-500/50 dark:to-orange-400/50 text-orange-600 dark:text-orange-300 mb-2">
                      <Flag className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                      {t('home:about.stats.countries')}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('home:about.stats.countriesLabel')}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column: Core Values in 2x2 Grid */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-3 h-full"
            >
              {values.map((value, index) => (
                <div
                  key={value.titleKey}
                  className="group bg-white dark:bg-slate-900 rounded-lg p-4 text-center hover:shadow-lg transition-all duration-300 border border-gray-100 dark:border-slate-800 hover:border-primary-200 dark:hover:border-primary-800 hover:-translate-y-1 flex flex-col"
                >
                  <div className="flex items-center justify-center w-full mb-3">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-primary-200 to-primary-300 dark:from-primary-500/50 dark:to-primary-400/50 text-primary-600 dark:text-primary-300 group-hover:scale-110 transition-transform duration-300">
                      {value.icon}
                    </div>
                  </div>
                  <h4 className="text-base font-semibold mb-2 text-gray-900 dark:text-white">
                    {t(value.titleKey)}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed flex-1">
                    {t(value.descriptionKey)}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* Products Section - Compact Design */}
      <Section id="products" className="bg-gradient-to-br from-gray-50 via-white to-accent-100/50 dark:from-slate-900 dark:via-slate-800 dark:to-accent-950/50 relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 opacity-50 dark:opacity-40">
          <div className="absolute bottom-20 left-0 w-[500px] h-[500px] bg-gradient-to-br from-accent-300 to-yellow-300 dark:from-accent-500/50 dark:to-yellow-500/50 rounded-full filter blur-3xl" />
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-tl from-green-300 to-teal-300 dark:from-green-500/50 dark:to-teal-500/50 rounded-full filter blur-3xl" />
        </div>

        <Container className="relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 dark:text-white leading-tight">
              {t('home:products.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-300">
              {t('home:products.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={product.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-5 h-full flex flex-col hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-slate-700 hover:border-primary-200 dark:hover:border-primary-700 hover:-translate-y-1">
                  {/* Icon and Status Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <div className={`bg-gradient-to-r ${product.gradient} text-white p-3 rounded-xl group-hover:scale-110 transition-transform duration-300`}>
                      {product.icon}
                    </div>
                    {product.status === 'coming-soon' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent-100 dark:bg-accent-900/30 text-accent-800 dark:text-accent-400">
                        <Star className="mr-1 w-2.5 h-2.5" />
                        {t('common.comingSoon', { ns: 'translation' })}
                      </span>
                    )}
                  </div>

                  {/* Title and Tagline */}
                  <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                    {t(product.titleKey)}
                  </h3>
                  <p className="text-sm text-primary-600 dark:text-primary-400 font-semibold mb-2">
                    {t(product.taglineKey)}
                  </p>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 text-xs mb-4 flex-grow leading-relaxed">
                    {t(product.descriptionKey)}
                  </p>

                  {/* Learn More Button */}
                  {product.isInternal && product.status === 'active' ? (
                    <Link
                      to={product.url}
                      className={`inline-flex items-center justify-center w-full px-6 py-3 rounded-lg font-semibold transition-all duration-300 bg-gradient-to-r ${product.gradient} text-white hover:shadow-lg hover:scale-105`}
                    >
                      {t('common.learnMore', { ns: 'translation' })}
                    </Link>
                  ) : (
                    <a
                      href={product.url}
                      target={product.url.startsWith('http') ? '_blank' : '_self'}
                      rel={product.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className={`inline-flex items-center justify-center w-full px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                        product.status === 'active'
                          ? `bg-gradient-to-r ${product.gradient} text-white hover:shadow-lg hover:scale-105`
                          : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                      }`}
                      onClick={(e) => {
                        if (product.status === 'coming-soon') {
                          e.preventDefault()
                        }
                      }}
                    >
                      {product.status === 'active' ? t('common.learnMore', { ns: 'translation' }) : t('common.comingSoon', { ns: 'translation' })}
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Careers Section - Ultra Compact */}
      <Section id="careers" className="bg-gradient-to-br from-white via-gray-50 to-purple-100/50 dark:from-slate-900 dark:via-slate-800 dark:to-purple-950/50 relative overflow-hidden py-12">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 opacity-40 dark:opacity-30">
          <div className="absolute top-10 left-1/2 w-[400px] h-[400px] bg-gradient-to-br from-primary-300 to-purple-300 dark:from-primary-500/50 dark:to-purple-500/50 rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-gradient-to-tl from-pink-300 to-rose-300 dark:from-pink-500/50 dark:to-rose-500/50 rounded-full filter blur-3xl" />
        </div>

        <Container className="relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 dark:text-white leading-tight">
              {t('home:careers.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-300">
              {t('home:careers.subtitle')}
            </p>
          </motion.div>

          {/* Benefits - Horizontal Compact Layout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-gray-100 dark:border-slate-800 p-5 mb-6"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {benefits.map((benefit, index) => (
                <div
                  key={benefit.titleKey}
                  className="text-center group"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-primary-200 to-primary-300 dark:from-primary-500/50 dark:to-primary-400/50 text-primary-600 dark:text-primary-300 mb-2 group-hover:scale-110 transition-transform duration-300">
                    {benefit.icon}
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {t(benefit.titleKey)}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-tight">{t(benefit.descriptionKey)}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Job Postings Section - Compact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className=""
          >
            <h3 className="text-2xl font-bold mb-2 text-center text-gray-900 dark:text-white">
              {t('home:careers.positions.title')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              {t('home:careers.positions.subtitle')}
            </p>

            {/* Job Postings or Empty State */}
            {jobPostings.length > 0 ? (
              <div className="space-y-4">
                {jobPostings.map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-white dark:bg-slate-900 rounded-xl shadow-md border border-gray-100 dark:border-slate-800 hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-lg transition-all duration-300 overflow-hidden"
                  >
                    {/* Job Header */}
                    <div className="p-5">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                        <div className="flex-grow">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold">
                              {t('home:careers.nowHiring')}
                            </span>
                          </div>
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {t(`${job.i18nKey}.title`)}
                          </h4>
                          <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400 mb-3">
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5" />
                              {t(`${job.i18nKey}.department`)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {t(`${job.i18nKey}.location`)}
                            </span>
                            <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full font-medium">
                              {t(`${job.i18nKey}.type`)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                            {t(`${job.i18nKey}.description`)}
                          </p>
                        </div>
                        <div className="flex flex-row md:flex-col gap-2 md:min-w-[130px]">
                          {job.isOpen ? (
                            <button
                              onClick={() => setSelectedJob(job.id)}
                              className="flex-1 md:flex-none px-4 py-2.5 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap text-sm text-center"
                            >
                              {t('common.applyNow', { ns: 'translation' })}
                            </button>
                          ) : (
                            <span className="flex-1 md:flex-none px-4 py-2.5 bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-500 rounded-lg font-semibold whitespace-nowrap text-sm text-center cursor-not-allowed">
                              {t('home:careers.positionClosed')}
                            </span>
                          )}
                          <button
                            onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                            className="flex-1 md:flex-none px-4 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200 text-sm flex items-center justify-center gap-1"
                          >
                            {expandedJob === job.id
                              ? <><ChevronUp className="w-4 h-4" />{t('home:careers.less')}</>
                              : <><ChevronDown className="w-4 h-4" />{t('home:careers.details')}</>}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {expandedJob === job.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 pb-6 border-t border-gray-100 dark:border-slate-800 pt-5 space-y-5">
                            {/* Responsibilities */}
                            <div>
                              <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
                                {t('home:careers.application.responsibilities')}
                              </h5>
                              <ul className="space-y-1.5">
                                {(t(`${job.i18nKey}.responsibilities`, { returnObjects: true }) as string[]).map((r, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                                    {r}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {/* Required */}
                            <div>
                              <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
                                {t('home:careers.application.requiredQualifications')}
                              </h5>
                              <ul className="space-y-1.5">
                                {(t(`${job.i18nKey}.required`, { returnObjects: true }) as string[]).map((r, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    {r}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {/* Nice to Have */}
                            <div>
                              <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
                                {t('home:careers.application.niceToHave')}
                              </h5>
                              <ul className="space-y-1.5">
                                {(t(`${job.i18nKey}.niceToHave`, { returnObjects: true }) as string[]).map((r, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    <Star className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                    {r}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {/* Tech Stack */}
                            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3">
                              <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-1 uppercase tracking-wide">
                                {t('home:careers.application.techStack')}
                              </h5>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {t(`${job.i18nKey}.techStack`)}
                              </p>
                            </div>
                            {/* What We Offer */}
                            <div>
                              <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
                                {t('home:careers.application.whatWeOffer')}
                              </h5>
                              <ul className="space-y-1.5">
                                {(t(`${job.i18nKey}.whatWeOffer`, { returnObjects: true }) as string[]).map((r, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    <Heart className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                                    {r}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {job.isOpen ? (
                              <button
                                onClick={() => setSelectedJob(job.id)}
                                className="w-full py-3 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300 text-sm"
                              >
                                {t('home:careers.applyForPosition')}
                              </button>
                            ) : (
                              <div className="w-full py-3 bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-500 rounded-lg font-semibold text-sm text-center">
                                {t('home:careers.positionClosed')}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 text-center border border-gray-200 dark:border-slate-700"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-primary-200 to-primary-300 dark:from-primary-500/50 dark:to-primary-400/50 text-primary-600 dark:text-primary-300 mb-3">
                  <Briefcase className="w-6 h-6" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {t('home:careers.noPositions.title')}
                </h4>
                <p className="text-base text-gray-600 dark:text-gray-400 mb-4">
                  {t('home:careers.noPositions.description')}
                </p>
                <button
                  onClick={() => document.getElementById('newsletter')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex px-5 py-2 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-600 dark:from-primary-500 dark:via-primary-600 dark:to-accent-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 text-sm"
                >
                  {t('home:careers.noPositions.cta')}
                </button>
              </motion.div>
            )}
          </motion.div>
        </Container>
      </Section>

      {/* Newsletter Section */}
      <Section id="newsletter" className="bg-gradient-to-br from-primary-500 via-primary-600 to-accent-600 dark:from-primary-600 dark:via-primary-700 dark:to-accent-700 relative overflow-hidden">
        {/* Animated background blobs */}
        <div className="absolute inset-0 opacity-20">
          <motion.div
            className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
            animate={{
              x: [0, 50, 0],
              y: [0, 30, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute bottom-0 left-0 w-80 h-80 bg-accent-400 rounded-full filter blur-3xl"
            animate={{
              x: [0, -40, 0],
              y: [0, -40, 0],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        <Container className="relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm text-white mb-5">
              <Mail className="w-6 h-6" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              {t('home:newsletter.title')}
            </h2>
            <p className="text-base md:text-lg text-white/90 mb-6">
              {t('home:newsletter.subtitle')}
            </p>

            <form onSubmit={handleNewsletterSubmit} className="max-w-lg mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="flex-grow px-6 py-4 bg-white dark:bg-white/95 text-gray-900 rounded-lg focus:ring-4 focus:ring-white/50 focus:outline-none transition-all"
                  placeholder={t('home:newsletter.placeholder')}
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-white/90 transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap"
                >
                  {t('home:newsletter.button')}
                </button>
              </div>

              {newsletterStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white"
                >
                  {t('home:newsletter.successMessage')}
                </motion.div>
              )}

              {newsletterStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-red-500/20 backdrop-blur-sm border border-red-300/30 rounded-lg text-white"
                >
                  {t('home:newsletter.errorMessage')}
                </motion.div>
              )}
            </form>

            <p className="mt-6 text-sm text-white/70">
              {t('home:newsletter.privacy')}
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* Contact Section - Modern Split Layout */}
      <Section id="contact" className="bg-gradient-to-br from-gray-50 via-white to-accent-100/50 dark:from-slate-800 dark:via-slate-900 dark:to-accent-950/50 relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 opacity-40 dark:opacity-30">
          <div className="absolute top-0 right-1/3 w-[400px] h-[400px] bg-gradient-to-br from-accent-300 to-orange-300 dark:from-accent-500/50 dark:to-orange-500/50 rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-cyan-300 to-blue-300 dark:from-cyan-500/50 dark:to-blue-500/50 rounded-full filter blur-3xl" />
        </div>

        <Container className="relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 dark:text-white leading-tight">
              {t('home:contact.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-300">
              {t('home:contact.subtitle')}
            </p>
          </motion.div>

          {/* Split Layout: Form (Left) | Contact Info (Right) */}
          <div className="grid lg:grid-cols-5 gap-4 items-stretch">
            {/* Left Side: Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3 flex"
            >
              <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl p-4 border border-gray-100 dark:border-slate-800 backdrop-blur-sm w-full flex flex-col">
                <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                  {t('home:contact.form.title')}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-3 flex-1 flex flex-col">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {t('home:contact.form.name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder={t('home:contact.form.namePlaceholder')}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {t('home:contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder={t('home:contact.form.emailPlaceholder')}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {t('home:contact.form.subject')} *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder={t('home:contact.form.subjectPlaceholder')}
                    />
                  </div>

                  <div className="flex-1">
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {t('home:contact.form.message')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none h-full"
                      placeholder={t('home:contact.form.messagePlaceholder')}
                    />
                  </div>

                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-800 dark:text-green-400 text-sm"
                    >
                      {t('home:contact.form.successMessage')}
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-800 dark:text-red-400 text-sm"
                    >
                      {t('home:contact.form.errorMessage')}
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-2.5 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-600 dark:from-primary-500 dark:via-primary-600 dark:to-accent-500 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed text-sm mt-2"
                  >
                    {isSubmitting ? t('common.sending', { ns: 'translation' }) : t('common.sendMessage', { ns: 'translation' })}
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Right Side: Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2 flex flex-col gap-4 h-full"
            >
              {/* Contact Info Cards */}
              <div className="bg-gradient-to-br from-primary-100 to-accent-100 dark:from-slate-900 dark:to-slate-800 rounded-xl p-4 shadow-lg border border-primary-200 dark:border-slate-700 flex-1 flex flex-col">
                <h3 className="text-base font-bold mb-3 text-gray-900 dark:text-white">
                  {t('home:contact.info.title')}
                </h3>

                <div className="space-y-3 flex-1">
                  {/* Email */}
                  <div className="flex items-start gap-3 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-blue-300 dark:from-blue-500/50 dark:to-blue-400/50 flex items-center justify-center text-blue-600 dark:text-blue-300 group-hover:scale-110 transition-transform duration-300">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        {t('home:contact.info.email')}
                      </h4>
                      <a
                        href="mailto:info@bassilichat.com"
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline break-all"
                      >
                        info@bassilichat.com
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-3 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-green-200 to-green-300 dark:from-green-500/50 dark:to-green-400/50 flex items-center justify-center text-green-600 dark:text-green-300 group-hover:scale-110 transition-transform duration-300">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        {t('home:contact.info.phone')}
                      </h4>
                      <a
                        href="tel:+15149133612"
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        +1 (514) 913-3612
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-3 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-orange-200 to-orange-300 dark:from-orange-500/50 dark:to-orange-400/50 flex items-center justify-center text-orange-600 dark:text-orange-300 group-hover:scale-110 transition-transform duration-300">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        {t('home:contact.info.location')}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
                        {t('home:contact.info.address')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Info Card */}
              <div className="bg-white dark:bg-slate-900 rounded-xl p-4 shadow-lg border border-gray-100 dark:border-slate-800 flex-1 flex flex-col">
                <h4 className="text-sm font-bold mb-2 text-gray-900 dark:text-white">
                  {t('home:contact.info.responseTime')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                  {t('home:contact.info.responseDescription')}
                </p>
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* Job Application Modal */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) { setSelectedJob(null); setAppStatus('idle') } }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              {/* Modal Header */}
              <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-600 rounded-t-2xl p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-primary-100 text-xs font-semibold uppercase tracking-widest mb-1">
                      {t('home:careers.application.headerSubtitle')}
                    </p>
                    <h2 className="text-xl font-bold text-white leading-tight">
                      {(() => {
                        const job = jobPostings.find(j => j.id === selectedJob)
                        return job ? t(`${job.i18nKey}.title`) : ''
                      })()}
                    </h2>
                    <div className="flex flex-wrap gap-3 mt-2 text-primary-100 text-xs">
                      {(() => {
                        const job = jobPostings.find(j => j.id === selectedJob)
                        return job ? (
                          <>
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t(`${job.i18nKey}.location`)}</span>
                            <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{t(`${job.i18nKey}.type`)}</span>
                          </>
                        ) : null
                      })()}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedJob(null); setAppStatus('idle') }}
                    className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors flex-shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {appStatus === 'success' ? (
                <div className="p-10 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mb-4">
                    <CheckCircle className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {t('home:careers.application.successTitle')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm leading-relaxed">
                    {t('home:careers.application.successMessage')}
                  </p>
                  <button
                    onClick={() => { setSelectedJob(null); setAppStatus('idle') }}
                    className="px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
                  >
                    {t('home:careers.application.close')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleAppSubmit} className="p-6 space-y-6">

                  {/* Personal Information */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-primary-500" /> {t('home:careers.application.sections.personalInfo')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.fullName')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          required
                          value={appForm.fullName}
                          onChange={handleAppChange}
                          placeholder={t('home:careers.application.placeholders.fullName')}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.email')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={appForm.email}
                          onChange={handleAppChange}
                          placeholder={t('home:careers.application.placeholders.email')}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.phone')}
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={appForm.phone}
                          onChange={handleAppChange}
                          placeholder={t('home:careers.application.placeholders.phone')}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.currentRole')}
                        </label>
                        <input
                          type="text"
                          name="currentRole"
                          value={appForm.currentRole}
                          onChange={handleAppChange}
                          placeholder={t('home:careers.application.placeholders.currentRole')}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Professional Links */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-primary-500" /> {t('home:careers.application.sections.professionalLinks')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.linkedIn')}
                        </label>
                        <input
                          type="url"
                          name="linkedIn"
                          value={appForm.linkedIn}
                          onChange={handleAppChange}
                          placeholder={t('home:careers.application.placeholders.linkedIn')}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.github')}
                        </label>
                        <input
                          type="url"
                          name="github"
                          value={appForm.github}
                          onChange={handleAppChange}
                          placeholder={t('home:careers.application.placeholders.github')}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.portfolio')}
                        </label>
                        <input
                          type="text"
                          name="portfolio"
                          value={appForm.portfolio}
                          onChange={handleAppChange}
                          placeholder={t('home:careers.application.placeholders.portfolio')}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Application Details */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                      <FolderKanban className="w-4 h-4 text-primary-500" /> {t('home:careers.application.sections.applicationDetails')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.yearsExperience')} <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="yearsExperience"
                          required
                          value={appForm.yearsExperience}
                          onChange={handleAppChange}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        >
                          <option value="">{t('home:careers.application.options.experience.placeholder')}</option>
                          <option value="1-2 years">{t('home:careers.application.options.experience.1-2')}</option>
                          <option value="3-5 years">{t('home:careers.application.options.experience.3-5')}</option>
                          <option value="5-7 years">{t('home:careers.application.options.experience.5-7')}</option>
                          <option value="7-10 years">{t('home:careers.application.options.experience.7-10')}</option>
                          <option value="10+ years">{t('home:careers.application.options.experience.10+')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.noticePeriod')}
                        </label>
                        <select
                          name="noticePeriod"
                          value={appForm.noticePeriod}
                          onChange={handleAppChange}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        >
                          <option value="">{t('home:careers.application.options.noticePeriod.placeholder')}</option>
                          <option value="Immediately available">{t('home:careers.application.options.noticePeriod.immediate')}</option>
                          <option value="2 weeks">{t('home:careers.application.options.noticePeriod.2weeks')}</option>
                          <option value="1 month">{t('home:careers.application.options.noticePeriod.1month')}</option>
                          <option value="2 months">{t('home:careers.application.options.noticePeriod.2months')}</option>
                          <option value="3+ months">{t('home:careers.application.options.noticePeriod.3months+')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.workAuthorization')} <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="workAuthorization"
                          required
                          value={appForm.workAuthorization}
                          onChange={handleAppChange}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        >
                          <option value="">{t('home:careers.application.options.workAuth.placeholder')}</option>
                          <option value="Canadian Citizen">{t('home:careers.application.options.workAuth.citizen')}</option>
                          <option value="Permanent Resident">{t('home:careers.application.options.workAuth.pr')}</option>
                          <option value="Work Permit">{t('home:careers.application.options.workAuth.permit')}</option>
                          <option value="Requires Sponsorship">{t('home:careers.application.options.workAuth.sponsorship')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.salaryExpectation')}
                        </label>
                        <input
                          type="text"
                          name="salaryExpectation"
                          value={appForm.salaryExpectation}
                          onChange={handleAppChange}
                          placeholder={t('home:careers.application.placeholders.salaryExpectation')}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.fields.heardAboutUs')}
                        </label>
                        <select
                          name="heardAboutUs"
                          value={appForm.heardAboutUs}
                          onChange={handleAppChange}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition"
                        >
                          <option value="">{t('home:careers.application.options.heardAboutUs.placeholder')}</option>
                          <option value="LinkedIn">{t('home:careers.application.options.heardAboutUs.linkedin')}</option>
                          <option value="Indeed">{t('home:careers.application.options.heardAboutUs.indeed')}</option>
                          <option value="Company Website">{t('home:careers.application.options.heardAboutUs.website')}</option>
                          <option value="Referral">{t('home:careers.application.options.heardAboutUs.referral')}</option>
                          <option value="GitHub">{t('home:careers.application.options.heardAboutUs.github')}</option>
                          <option value="Other">{t('home:careers.application.options.heardAboutUs.other')}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Cover Letter */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-primary-500" /> {t('home:careers.application.sections.coverLetter')}
                    </h3>
                    <textarea
                      name="coverLetter"
                      value={appForm.coverLetter}
                      onChange={handleAppChange}
                      rows={5}
                      placeholder={t('home:careers.application.placeholders.coverLetter')}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none transition resize-none"
                    />
                  </div>

                  {/* Document Links */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-4 flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-primary-500" /> {t('home:careers.application.sections.fileUploads')}
                    </h3>
                    <div className="space-y-3">
                      {/* Resume Link */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.uploads.resumeLabel')} <span className="text-red-500">*</span>
                          <span className="ml-1 text-gray-400 font-normal">{t('home:careers.application.uploads.resumeHint')}</span>
                        </label>
                        <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-lg border transition-all duration-200 ${resumeLink.trim() ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'} focus-within:ring-2 focus-within:ring-primary-400 focus-within:border-transparent`}>
                          {resumeLink.trim() ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <input
                            type="url"
                            value={resumeLink}
                            onChange={(e) => setResumeLink(e.target.value)}
                            placeholder={t('home:careers.application.uploads.resumePrompt')}
                            className="flex-1 bg-transparent text-gray-900 dark:text-white text-sm outline-none placeholder:text-gray-400"
                          />
                        </div>
                      </div>

                      {/* Cover Letter Link */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('home:careers.application.uploads.coverLetterLabel')} <span className="text-gray-400 font-normal">{t('home:careers.application.uploads.coverLetterHint')}</span>
                        </label>
                        <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-lg border transition-all duration-200 ${coverLetterLink.trim() ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'} focus-within:ring-2 focus-within:ring-primary-400 focus-within:border-transparent`}>
                          {coverLetterLink.trim() ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <input
                            type="url"
                            value={coverLetterLink}
                            onChange={(e) => setCoverLetterLink(e.target.value)}
                            placeholder={t('home:careers.application.uploads.coverLetterPrompt')}
                            className="flex-1 bg-transparent text-gray-900 dark:text-white text-sm outline-none placeholder:text-gray-400"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Error state */}
                  {appStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm"
                    >
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{t('home:careers.application.errorMessage')} <a href="mailto:careers@bassilichat.com" className="underline">careers@bassilichat.com</a>.</span>
                    </motion.div>
                  )}

                  {/* Submit */}
                  <div className="pt-2">
                    {(() => {
                      const job = jobPostings.find(j => j.id === selectedJob)
                      if (!job?.isOpen) {
                        return (
                          <div className="w-full py-4 bg-gray-100 dark:bg-slate-800 rounded-xl text-center">
                            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                              {t('home:careers.application.positionClosed')}
                            </p>
                          </div>
                        )
                      }
                      return (
                        <>
                          {!resumeLink.trim() && (
                            <p className="text-xs text-red-500 mb-3 flex items-center gap-1">
                              <AlertCircle className="w-3.5 h-3.5" /> {t('home:careers.application.resumeRequired')}
                            </p>
                          )}
                          <button
                            type="submit"
                            disabled={appSubmitting || !resumeLink.trim()}
                            className="w-full py-3.5 bg-gradient-to-r from-primary-500 via-primary-600 to-accent-600 text-white rounded-xl font-bold text-base hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {appSubmitting ? (
                              <><span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {t('home:careers.application.submitting')}</>
                            ) : (
                              <>{t('home:careers.application.submitButton')} <ArrowRight className="w-5 h-5" /></>
                            )}
                          </button>
                          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3">
                            {t('home:careers.application.privacyNote')}
                          </p>
                        </>
                      )
                    })()}
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
